// External imports
import React, { useContext, useEffect, useState } from 'react'
import { AiFillWarning } from '@react-icons/all-files/ai/AiFillWarning'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'

// Internal imports
import AddressBar from 'components/common/addressBar/AddressBar'
import Button from 'components/common/button'
import MapComponent from 'components/common/maps/MapComponent'
import { Spinner } from 'components/common/spinner'
import TnpChange from 'components/tnpChange'
import { RouteConstants } from 'config/routes/routeConstants'
import { LanguageContext } from 'context/LanguageContext'
import { LayoutContext } from 'context/LayoutContext'
import { TermsContext } from 'context/TermsContext'
import { trackLocationConfirmedResult } from 'middleware/helper'
import { patchUpdatedTermsAndPrivacy } from 'modules/authentication/actions'
import { clearBasket } from 'modules/basket/actions'
import { getConfiguration } from 'modules/configuration/actions'
import {
    getStoresByLocation,
    verifyPlace,
    setTempAddress,
    setTempStoreDetails,
    setIsAddressDeliverable,
    filterStoresByState,
    setSavedAddresses,
    setClearCartMessage,
} from 'modules/location/actions'
import { detailsFromPlaceId, detailsFromCoordinates, checkForStores } from 'modules/location/sagas'
import { getCurrentOrdersList } from 'modules/orders/actions'
import { setUserProfile } from 'modules/user/actions'
import { getUserProfileAsync } from 'modules/user/sagas'
import { getSavedCards } from 'modules/wallet/actions'
import { util } from 'utils'
import { countryCode, getJWTDetails, isEmpty, orderTypes } from 'utils/appUtils'
import constants from 'utils/constants'
import Util from 'utils/Util'
import { getHomePageItems } from 'modules/products/actions'
import { getCDNURL } from 'network/api/networkSettings'

const EnterAddress = ({
    deliveryAddress,
    pickupAddress,
    setDeliveryAddress,
    nextForm,
    formState,
    children,
    prevForm,
    hide,
    isRegistration,
    setPickupAddress,
    setShowRegister,
    setShouldTransferTemp,
}) => {
    const { strings } = useContext(LanguageContext)

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const [loader, setLoader] = useState(false)
    const { manageModal } = useContext(TermsContext)
    const enterAddressCheck = formState === constants.enterDeliveryAddressStep
    const confirmLocation = formState === constants.confirmDeliveryAddressStep
    const [addressIsValidated, updateAddressValidation] = useState(false)
    const [noStores, updateNoStores] = useState(false)
    const storeNearUser = useSelector(state => state.location.getIn(['storeNearUser']))
    const store = useSelector(state => state)
    const { panelOpen } = useContext(LayoutContext)
    const isFrom7RewardsView = useSelector(state => state.appSettings.get('isApp'))
    const [termsChanged, setTermsChanged] = useState(false)
    const selectedAddressInStore = useSelector(state => state.location.getIn(['selectedAddress']))
    const selectedAddress = selectedAddressInStore?.toJS() || null
    const basketItems = useSelector(state => state.basket.get('basketItems'))
    const itemsAddedToCart = basketItems?.toJS() || null
    const [showClearCartMsg, setShowClearCartMsg] = useState(false)
    const [tncArr, setTncArr] = useState([])
    const { isSignedIn } = getJWTDetails()
    const currentPath = location.pathname
    const routes = currentPath.split('/')
    const redirectHome = routes.length > 2 && (routes[1] === 'shop' || 'home')

    const currentPage = routes[routes.length - 1]
    const [showStoreChangeClearCartMsg, setShowStoreChangeClearCartMsg] = useState(false)

    const existingStore = useSelector(state => state.location.get('selectedStore'))
    const storeId = existingStore ? existingStore.toJS().store_id : null
    const getCartInProgress = useSelector(state => state.basket.get('getCartInProgress'))
    const patchInProgress = useSelector(state => state.basket.get('cartPatchInProgress'))
    const homePageItemsInProgress = useSelector(
        state => state.products['getHomePageItemsInProgress']
    )
    useEffect(() => {
        if (!Util.isAddressValid(deliveryAddress)) {
            updateAddressValidation(true)
        } else {
            updateAddressValidation(false)
        }
    }, [deliveryAddress['formatted_address']])

    useEffect(() => {
        if (storeNearUser && storeNearUser.length) {
            if (deliveryAddress.street) setPickupAddress({ ...deliveryAddress, type: 'pickup' })
            else setPickupAddress({ ...pickupAddress, type: 'pickup' })
        }
    }, [storeNearUser])
    useEffect(() => {
        return () => {
            dispatch(setClearCartMessage(false))
        }
    }, [])

    const selectAddressFromList = suggestion => {
        setLoader(true)
        detailsFromPlaceId(suggestion.place_id, orderTypes.DELIVERY).then(parsedAddress => {
            setDeliveryAddress({ ...deliveryAddress, ...parsedAddress })
            setLoader(false)
        })
        enterAddressCheck && nextForm()
    }

    const selectAddressFromSavedList = suggestion => {
        setDeliveryAddress(suggestion)
        enterAddressCheck && nextForm()
    }

    const selectAddressFromGeoLocation = coordinates => {
        setLoader(true)
        detailsFromCoordinates(coordinates, orderTypes.DELIVERY).then(parsedAddress => {
            setDeliveryAddress({ ...deliveryAddress, ...parsedAddress })
            setLoader(false)
        })
        enterAddressCheck && nextForm()
    }

    const handleAptChange = e => {
        setDeliveryAddress({ ...deliveryAddress, apt: e.target.value })
    }

    const clearCompleteBasket = () => {
        dispatch(clearBasket())
        handleConfirmLocation()
        setShowClearCartMsg(false)
        dispatch(setClearCartMessage(false))
        setShowStoreChangeClearCartMsg(false)
        setLoader(false)
    }

    const clearCartForStoreChange = () => {
        dispatch(clearBasket())
        setShowClearCartMsg(false)
        dispatch(setClearCartMessage(false))
        setShowStoreChangeClearCartMsg(false)
        setLoader(false)
    }

    const hideFeedback = () => {
        setShowClearCartMsg(false)
        dispatch(setClearCartMessage(false))
        setShowStoreChangeClearCartMsg(true)
        setLoader(false)
    }

    const handleBrowseOption = () => {
        if (location.pathname === '/') {
            navigate(RouteConstants.Home)
        }
        hide()
    }

    const handleGetUserProfile = () => {
        getUserProfileAsync()
            .then(res => {
                const { saved_address, profile } = res
                if (profile.required_terms.length) {
                    const tncArr = []
                    for (let element of profile.required_terms) {
                        if (element.type === 'privacy' || element.type === 'terms') {
                            tncArr.push(element)
                        }
                    }
                    if (tncArr.length) {
                        setTncArr(tncArr)
                        setTermsChanged(true)
                        setShouldTransferTemp(false)
                    }
                } else {
                    hide()
                }
                if (saved_address) {
                    const parsedSavedAddress = saved_address.map(each =>
                        util.getCommonFormattedAddress(each)
                    )
                    dispatch(setSavedAddresses(parsedSavedAddress))
                } else {
                    dispatch(setSavedAddresses([]))
                }
                const parsedProfile = util.parseUserProfile(profile)
                dispatch(setUserProfile(parsedProfile))
                setLoader(false)
            })
            .catch(e => {
                console.log('here for error', e)
            })
    }

    const handleConfirmLocationAtTNC = (termId, privacyId) => {
        setShouldTransferTemp(true)
        setTimeout(() => {
            dispatch(getStoresByLocation(deliveryAddress))
            const updatedTermsAndPrivacy = [
                ...(termId ? [termId.id] : []),
                ...(privacyId ? [privacyId.id] : []),
            ]
            dispatch(patchUpdatedTermsAndPrivacy(updatedTermsAndPrivacy))
            hide()
        }, 0)
    }

    // 1. check if basket is empty if not clear basket items
    // 2. After basket items are cleared then make the "verifyPlace" and "checkForStores"
    // 3. If the checkForStores is empty then show no store found content
    // 4. Now if the user is signed in get user profile to check for "required_terms" based on that show TNC content in modal
    // 5. If they accept set the selected address and make the subsequent calls )ex: config, home page etc)
    // 6. If they're a signed in user and don't accept the TNC then give an option to cancel that flow
    // 7. If user is in conversion flow guest to signin don't accept the TNC then sign out the user

    const handleConfirmLocation = () => {
        setLoader(true)
        if (deliveryAddress?.country === countryCode.US) {
            dispatch(verifyPlace(deliveryAddress))
        }
        checkForStores(deliveryAddress)
            .then(res => {
                if (res?.stores?.length > 0) {
                    if (itemsAddedToCart.length > 0 && res.stores[0].store_id !== storeId) {
                        setShowClearCartMsg(true)
                        dispatch(setClearCartMessage(true))
                        setShowStoreChangeClearCartMsg(true)
                        setLoader(false)
                        return
                    }
                    trackLocationConfirmedResult(true, deliveryAddress, store)
                    if (
                        isEmpty(selectedAddress) ||
                        (selectedAddress && deliveryAddress?.country !== selectedAddress?.country)
                    ) {
                        dispatch(setTempStoreDetails(res.stores[0]))
                        dispatch(setTempAddress(deliveryAddress))
                        dispatch(getConfiguration())
                    } else {
                        dispatch(getStoresByLocation(deliveryAddress))
                        setLoader(false)
                    }
                    if (!isRegistration) {
                        if (isSignedIn && deliveryAddress?.country !== selectedAddress?.country) {
                            handleGetUserProfile()
                            manageModal(false)
                            dispatch(getCurrentOrdersList())
                            if (currentPage === 'order-details' || currentPage === 'order-status') {
                                navigate('/home')
                            }
                        } else {
                            dispatch(getHomePageItems())
                            hide()
                        }
                    } else {
                        dispatch(setTempStoreDetails(res.stores[0]))
                        dispatch(setTempAddress(deliveryAddress))
                        nextForm()
                    }
                    dispatch(setIsAddressDeliverable(true))
                } else {
                    trackLocationConfirmedResult(false, deliveryAddress, store, 'no store found')
                    dispatch(setIsAddressDeliverable(false))
                    dispatch(filterStoresByState(deliveryAddress))
                    updateNoStores(true)
                    setLoader(false)
                }
            })
            .catch(() => {
                trackLocationConfirmedResult(false, deliveryAddress, store, 'network error')
                setLoader(false)
            })
    }
    const handleCheckCart = () => {
        if (itemsAddedToCart.length > 0 && deliveryAddress?.country !== selectedAddress?.country) {
            setShowClearCartMsg(true)
            dispatch(setClearCartMessage(true))
            return
        }
        handleConfirmLocation()
    }

    if (showClearCartMsg) {
        return (
            <div
                className={'feedBackForm'}
                tabIndex="0"
                aria-label={`${strings.basketItemsRemovalWarning} ${strings.basketItemsRemovalDetailedWarning} `}
            >
                <div className={'feedBackForm__content'}>
                    <AiFillWarning className={'feedBackForm__content-warningIcon'} />
                    <p className={'feedBackForm__content-warningMsg1'}>
                        {strings.basketItemsRemovalWarning}
                    </p>
                    <p className={'feedBackForm__content-warningMsg2'}>
                        {showStoreChangeClearCartMsg
                            ? strings.storeChangeClearCartMsg
                            : strings.countryChangeClearCartMsg}
                    </p>
                </div>
                <div className={'feedBackForm__action'}>
                    <Button
                        className={'cancel cancel-lg'}
                        onClick={hideFeedback}
                        aria-labelledby="cancel button"
                    >
                        Cancel
                    </Button>
                    <Button
                        className={'confirm confirm-lg'}
                        onClick={
                            showStoreChangeClearCartMsg
                                ? clearCartForStoreChange
                                : clearCompleteBasket
                        }
                        aria-labelledby="clear basket button"
                    >
                        {strings.clearBasket}
                    </Button>
                </div>
            </div>
        )
    }

    const closeOptions = () => (panelOpen ? setShowRegister(false) : hide())
    if (loader) {
        return <div className={'enterAddressForm'} style={{justifyContent: 'center'}}><Spinner height={'5rem'} width={'5rem'} /></div>
    }

    if (getCartInProgress || patchInProgress || homePageItemsInProgress) {
        return <div className={'enterAddressForm'} style={{justifyContent: 'center'}}><Spinner height={'5rem'} width={'5rem'} /></div>
    }

    return (
        <div className={'enterAddressForm'}>
            {termsChanged ? (
                <TnpChange
                    tncArr={tncArr}
                    handleConfirmLocationAtTNC={handleConfirmLocationAtTNC}
                />
            ) : (
                <>
                    {!confirmLocation && (
                        <p className={'enterAddressForm-heading'}>{strings.locationTitle}</p>
                    )}
                    {confirmLocation ? (
                        noStores || (noStores && storeNearUser.length) ? (
                            <p className={'enterAddressForm-heading'}>
                                {strings.outOfDeliveryAreaTitle}
                            </p>
                        ) : (
                            <p className={'enterAddressForm-heading'}>
                                {strings.confirmLocationTitle}
                            </p>
                        )
                    ) : null}

                    {!confirmLocation && (
                        <p className={'enterAddressForm-sub'}>{strings.deliveryTitle}</p>
                    )}
                    {confirmLocation ? (
                        noStores || (noStores && storeNearUser.length) ? (
                            <p className={'enterAddressForm-sub'}>
                                {strings.outOfServiceAreaTitle}
                            </p>
                        ) : (
                            <p className={'enterAddressForm-sub'}>{strings.deliveryTitle}</p>
                        )
                    ) : null}
                    {children}
                    {noStores ? (
                        <>
                            <div className={'enterAddressForm__noStoresContent'}>
                                {storeNearUser.length ? (
                                    <>
                                        <div
                                            className={'enterAddressForm__noStoresContent-wrapper'}
                                        >
                                            <p className={'enterAddressForm__noStoresContent-p1'}>
                                                {strings.apology}
                                            </p>
                                            <img
                                                className={
                                                    'enterAddressForm__noStoresContent-arrowImg'
                                                }
                                                src={getCDNURL() + 'assets/images/arrow-bg.png'}
                                                alt={strings.arrowImg}
                                            ></img>
                                        </div>
                                        <p className={'enterAddressForm__noStoresContent-p2'}>
                                            {strings.outOfDeliveryArea}
                                        </p>
                                        <p className={'enterAddressForm__noStoresContent-p3'}>
                                            {strings.foundPickupOptions}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <p
                                            className={
                                                'enterAddressForm__noStoresContent-errorText'
                                            }
                                        >
                                            {strings.noStoresError}
                                        </p>
                                        <p
                                            className={
                                                'enterAddressForm__noStoresContent-suggestText'
                                            }
                                        >
                                            {strings.noStoresSuggestion}
                                        </p>
                                        <a
                                            className={'enterAddressForm__noStoresContent-link'}
                                            onClick={handleBrowseOption}
                                        >
                                            {strings.noStoresBrowse}
                                        </a>
                                    </>
                                )}
                            </div>
                            <div
                                className={
                                    'enterAddressForm-actionBtns enterAddressForm-actionBtns-1'
                                }
                            >
                                {noStores && storeNearUser?.length ? (
                                    // TO DO retry should be shown but the step form has no prev steps
                                    <Button className={'cancel cancel-xl'} onClick={closeOptions}>
                                        {strings.cancelButton}
                                    </Button>
                                ) : (
                                    <Button className={'cancel cancel-xl'} onClick={closeOptions}>
                                        {strings.cancelButton}
                                    </Button>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            {confirmLocation && (
                                //TODO lazy load the map as it's taking a lot to update
                                <div className={'enterAddressForm-map'}>
                                    <MapComponent selectedAddress={deliveryAddress} />
                                </div>
                            )}
                            <AddressBar
                                id={'deliveryModal'}
                                styleChange={
                                    confirmLocation ? 'confirmAddressModal' : 'modalAddressComp'
                                }
                                selectedAddress={deliveryAddress}
                                selectAddressFromList={selectAddressFromList}
                                selectAddressFromSavedList={selectAddressFromSavedList}
                                selectAddressFromGeoLocation={selectAddressFromGeoLocation}
                                isPickup={false}
                                isRegistration={isRegistration}
                                setLoader={setLoader}
                            />
                            {confirmLocation && (
                                <input
                                    className={'enterAddressForm-input'}
                                    name="apt"
                                    value={deliveryAddress.apt || ''}
                                    onChange={handleAptChange}
                                    placeholder={strings.ADDRESS_DELIVERY_APARTMENT_OR_SUITE}
                                    type="text"
                                />
                            )}
                            {enterAddressCheck && (
                                <div
                                    className={
                                        'enterAddressForm-actionBtns enterAddressForm-actionBtns-1'
                                    }
                                >
                                    <Button className={'cancel cancel-lg'} onClick={closeOptions}>
                                        {strings.cancelButton}
                                    </Button>
                                </div>
                            )}
                            {!enterAddressCheck && (
                                <div
                                    className={`enterAddressForm-actionBtns enterAddressForm-actionBtns-2 ${
                                        confirmLocation &&
                                        'enterAddressForm-actionBtns-confirmAddress'
                                    } `}
                                >
                                    {confirmLocation ? (
                                        isFrom7RewardsView ? (
                                            <Button
                                                className={'cancel cancel-lg'}
                                                onClick={closeOptions}
                                            >
                                                {strings.cancelButton}
                                            </Button>
                                        ) : (
                                            <Button
                                                className={'cancel cancel-lg'}
                                                onClick={prevForm}
                                            >
                                                {strings.cancelButton}
                                            </Button>
                                        )
                                    ) : (
                                        <Button
                                            className={'cancel cancel-lg'}
                                            onClick={closeOptions}
                                        >
                                            {strings.cancelButton}
                                        </Button>
                                    )}
                                    {confirmLocation && (
                                        <Button
                                            disabled={addressIsValidated}
                                            onClick={handleCheckCart}
                                            className={'confirm confirm-lg'}
                                        >
                                            {strings.confirmLocationButton}
                                        </Button>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    )
}

export default EnterAddress
